// ============================================================================
// OverviewStats (L16, F-830 + M-841) — 4 cartes de l'onglet « Apercu »
// ============================================================================
// Portage de components/profile/overview-stats.tsx : charge de travail / energie
// (12 mois en barres) / objectifs de l'annee / anciennete.
//
// SENSIBLE : le CALLSITE garantit le droit d'acces. Sur /profile/:email le serveur
// ne renvoie meme pas `overview` sans canReadMemberPulse — ce composant n'est donc
// monte que pour un ayant droit (fail-closed a la source, pas au rendu).
//
// MiniBarChart (PoC components/dashboard/mini-bar-chart) est HORS LOT : la barre
// de tendance est donc rendue inline en tokens (quelques div, aucune dependance) —
// porter un composant hors manifeste aurait ete du scope creep.

import { useTranslation } from '../../i18n';
import type { EmployeeObjective, MemberPulse } from '../../hooks/useProfile';

/**
 * Echelle du pouls individuel. Le PoC importe MEMBER_PULSE_MAX de
 * lib/member-pulse/questions (hors lot) ; la valeur est verrouillee cote base par
 * CK_gest_MemberPulse_workload / _energy (`BETWEEN 1 AND 5`, mig 144).
 */
const MEMBER_PULSE_MAX = 5;

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

/** Tendance 12 mois — equivalent inline du MiniBarChart hors lot. */
function TrendBars({ data, label }: { data: number[]; label: string }) {
  return (
    <div className="mt-2 flex h-7 items-end gap-0.5" role="img" aria-label={label}>
      {data.map((v, i) => (
        <div
          key={i}
          className="w-1.5 rounded-sm bg-brand-200 dark:bg-brand-900"
          style={{ height: `${Math.max(8, (v / MEMBER_PULSE_MAX) * 100)}%` }}
        />
      ))}
    </div>
  );
}

function StatTile({ label, value, hint, trend }: {
  label: string; value: string; hint: string; trend?: React.ReactNode;
}) {
  return (
    <div className={CARD}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-head font-bold tabular-nums text-slate-800 dark:text-slate-100">{value}</p>
      <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">{hint}</p>
      {trend}
    </div>
  );
}

export interface OverviewStatsProps {
  memberPulses: MemberPulse[];
  objectives: EmployeeObjective[];
  /** F-815 : null si le viewer n'a pas canReadCompensation (decide serveur). */
  hireDate: string | null;
}

export function OverviewStats({ memberPulses, objectives, hireDate }: OverviewStatsProps) {
  const { t } = useTranslation();

  // Le serveur renvoie les pouls en chrono INVERSE (12 derniers) — la frise se lit
  // dans le sens du temps, d'ou le tri croissant ici (parite PoC).
  const chrono = [...memberPulses].sort((a, b) => a.period.localeCompare(b.period));
  const workloadSeries = chrono.map((p) => p.workloadScore);
  const energySeries = chrono.map((p) => p.energyScore);
  const lastWorkload = workloadSeries.at(-1) ?? null;
  const lastEnergy = energySeries.at(-1) ?? null;

  const objectivesDone = objectives.filter((o) => o.status === 'done').length;
  const objectivesInProgress = objectives.filter((o) => o.status === 'in_progress').length;

  // M-841 : anciennete = annee courante - annee de hire_date, plancher 0. Calcul
  // calendaire simple, sans lib de date et SANS prorata jour/mois (verbatim PoC).
  const currentYear = new Date().getFullYear();
  const hireYear = hireDate ? Number(hireDate.slice(0, 4)) : null;
  const seniorityYears =
    hireYear !== null && Number.isFinite(hireYear) ? Math.max(0, currentYear - hireYear) : null;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile
        label={t('gest:profile.overview.workload')}
        value={lastWorkload !== null ? `${lastWorkload}/${MEMBER_PULSE_MAX}` : '—'}
        hint={t('gest:profile.overview.pulseHint')}
        trend={workloadSeries.length >= 2 ? <TrendBars data={workloadSeries} label={t('gest:profile.overview.workloadTrend')} /> : undefined}
      />
      <StatTile
        label={t('gest:profile.overview.energy')}
        value={lastEnergy !== null ? `${lastEnergy}/${MEMBER_PULSE_MAX}` : '—'}
        hint={t('gest:profile.overview.pulseHint')}
        trend={energySeries.length >= 2 ? <TrendBars data={energySeries} label={t('gest:profile.overview.energyTrend')} /> : undefined}
      />
      <StatTile
        label={t('gest:profile.overview.objectives', { year: currentYear })}
        value={objectives.length > 0 ? `${objectivesDone}/${objectives.length}` : '—'}
        hint={
          objectives.length > 0
            // `inProgress` et NON `count` : `count` declenche la pluralisation
            // i18next, qui eclate la cle en _one/_other/_many VIDES (piege deja
            // rencontre sur ce parc). Ici on veut une interpolation simple.
            ? t('gest:profile.overview.objectivesHint', { inProgress: objectivesInProgress })
            : t('gest:profile.overview.objectivesNone')
        }
      />
      <StatTile
        label={t('gest:profile.overview.seniority')}
        value={seniorityYears !== null ? t('gest:profile.overview.years', { count: seniorityYears }) : '—'}
        hint={hireDate ? t('gest:profile.overview.since', { date: hireDate.slice(0, 10) }) : t('gest:profile.overview.dateUnknown')}
      />
    </div>
  );
}
